/**
 * The goal compound's perimeter (03 §5.1 step 2): wall or wire on all four sides with the
 * planned gates left open - or no gates at all when the archetype wants it breached.
 *
 * East-west sides are ordinary runs - a wall in pieces of at most `WALLS.pieceMax` (ROUND7), so a breach
 * opens a gap a few pieces wide, as it does on the other two faces. The north-south sides are columns of
 * short pieces yawed a quarter turn; a quarter-turned run's box swaps its length and depth (props.ts), so
 * the pieces tile the side end to end. ROUND7: before that swap each piece's box lay across the wall, 1.1 m
 * thick and 0.7 m long, and the 0.4 m slits between them let eyes and bullets through a solid-looking wall.
 */

import { HALF_PI } from '../sim/math';
import { PROPS } from '../sim/props';
import type { Rect } from '../sim/types';
import type { GoalCtx, GoalResult } from './archetypes/types';
import type { MapBuilder } from './builder';
import { buildRun } from './obstacles';
import { COMPOUND } from './tuning';
import type { Gap, Gate, GenCtx } from './types';

/** A north-south column of `kind` from z0 to z1 at x, leaving `gaps` (by z) open. */
export function buildColumn(
  map: MapBuilder, kind: 'wall' | 'wire', x: number, z0: number, z1: number, gaps: readonly Gap[],
): void {
  const count = Math.max(1, Math.ceil((z1 - z0) / COMPOUND.columnPiece));
  const step = (z1 - z0) / count;
  for (let i = 0; i < count; i++) {
    const z = z0 + step * (i + 0.5);
    // A piece that would reach into a gate is left out, so the opening is never narrower than planned.
    if (gaps.some((g) => Math.abs(z - g.x) < g.width / 2 + step / 2)) continue;
    map.add(kind, x, z, { length: step, yaw: HALF_PI });
  }
}

export function buildPerimeter(map: MapBuilder, kind: 'wall' | 'wire', c: Rect, gates: readonly Gate[]): void {
  const half = COMPOUND.columnPiece / 2;
  const depth = PROPS[kind].runHalfDepth ?? 0.35;
  const south = gates.filter((g) => g.side === 'south').map((g) => ({ x: g.x, width: g.width }));
  // The north face was built solid whatever was asked of it. It still is, unless the Inside Man has
  // left the back gate open - the planner never puts one there, so a north gap can only be his.
  const north = gates.filter((g) => g.side === 'north').map((g) => ({ x: g.x, width: g.width }));
  buildRun(map, kind, c.z1, c.x0 - half, c.x1 + half, south);
  buildRun(map, kind, c.z0, c.x0 - half, c.x1 + half, north);
  // Columns start where the east-west runs' faces end, so each corner closes without an overlap.
  const inset = depth;
  for (const side of ['west', 'east'] as const) {
    const gaps = gates.filter((g) => g.side === side).map((g) => ({ x: g.z, width: g.width }));
    buildColumn(map, kind, side === 'west' ? c.x0 : c.x1, c.z0 + inset, c.z1 - inset, gaps);
  }
}

/** Perimeter first, then the archetype's compound grammar; returns its objective script. */
export function buildGoal(ctx: GenCtx): GoalResult {
  const { spec, plan, map } = ctx;
  if (spec.perimeter !== 'none' && spec.layout === 'compound') {
    // Only a north gate belongs to the perimeter; the Inside Man's other gate is a crossing in the belt.
    const back = plan.insideGate;
    const open = back !== null && back.side === 'north' ? [...plan.gates, back] : plan.gates;
    buildPerimeter(map, spec.perimeter, plan.compound, open);
  }
  const c = plan.compound;
  const inset = spec.perimeter === 'none' ? 0 : 3.5;
  const g: GoalCtx = {
    ctx,
    map,
    rng: ctx.rng.fork(`goal-${spec.id}`),
    kit: ctx.kit,
    threat: ctx.params.threat,
    rect: c,
    inner: { x0: c.x0 + inset, z0: c.z0 + inset, x1: c.x1 - inset, z1: c.z1 - inset },
    elite: ctx.elite,
  };
  return spec.build(g);
}
